import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { useTheme } from "../../../theme";

// Props for WeeklyZonesCompleted component
interface WeeklyZonesCompletedProps {
  totalZones?: number;
  completedZoneIds?: number[];
  isWeekOne?: boolean;
}

const WeeklyZonesCompleted: React.FC<WeeklyZonesCompletedProps> = ({
  totalZones = 0,
  completedZoneIds = [],
  isWeekOne = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const containerBg       = isDark ? colors.background.surface : '#FFFFFF';
  const titleColor        = isDark ? colors.text.inverse : '#2D2D2D';
  const badgeBg           = isDark ? colors.background.elevated : '#D4EDDA';
  const badgeTextColor    = isDark ? colors.text.inverse : '#155724';
  const tileCompletedBg   = isDark ? colors.background.elevated : '#D4EDDA';
  const tileCompletedText = isDark ? colors.text.inverse : '#155724';
  const tileIncompleteBg  = isDark ? colors.background.elevated : '#FFE5DE';
  const tileIncompleteText= isDark ? colors.text.inverse : '#C12C01';

  // Helper to get the list of zone numbers to display (excluding last or 6th as per rules)
  const getDisplayZoneNumbers = () => {
    if (totalZones === 6) {
      // Exclude the last zone
      return Array.from({ length: 5 }, (_, i) => i + 1);
    } else if (totalZones === 7) {
      // Exclude the 6th zone (zone number 6)
      return [1, 2, 3, 4, 5, 7];
    } else if (totalZones > 0) {
      // Default: show all zones
      return Array.from({ length: totalZones }, (_, i) => i + 1);
    }
    return [];
  };

  const displayZoneNumbers = getDisplayZoneNumbers();

  if (displayZoneNumbers.length === 0) {
    return null;
  }

  return (
    <View
      style={[
        styles.zonesCompletedContainer,
        {
          backgroundColor: containerBg,
          borderColor: isDark ? colors.border.default : 'transparent',
          borderWidth: isDark ? 1 : 0,
        },
      ]}
    >
      <View style={styles.titleRow}>
        <Text style={[styles.zonesCompletedTitle, { color: titleColor }]}>Weekly Zones Completed</Text>
        {isWeekOne && (
          <View style={[styles.weekOneBadge, { backgroundColor: badgeBg }]}>
            <Text style={[styles.weekOneBadgeText, { color: badgeTextColor }]}>Week 1 – No walk required</Text>
          </View>
        )}
      </View>
      <View style={styles.zonesRow}>
        {displayZoneNumbers.map((zoneNum) => {
          // Week 1: all zones are treated as completed (green)
          const isCompleted = isWeekOne || completedZoneIds.includes(zoneNum);
          return (
            <View
              key={zoneNum}
              testID={`zone-tile-${zoneNum}`}
              style={[
                styles.zoneTile,
                { backgroundColor: isCompleted ? tileCompletedBg : tileIncompleteBg },
              ]}
            >
              <Text
                style={[
                  styles.zoneTileText,
                  { color: isCompleted ? tileCompletedText : tileIncompleteText },
                ]}
              >
                {zoneNum}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  zonesCompletedContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 20,
    gap: 20,
    alignItems: "center",
    marginBottom: 16,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    alignSelf: "stretch",
  },
  zonesCompletedTitle: {
    fontFamily: "Inter",
    fontWeight: "700",
    fontSize: 16,
    lineHeight: 22, // 1.375em converted to px
    textAlign: "left",
    color: "#2D2D2D",
  },
  weekOneBadge: {
    backgroundColor: "#D4EDDA",
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 8,
  },
  weekOneBadgeText: {
    fontFamily: "Inter",
    fontWeight: "600",
    fontSize: 12,
    color: "#155724",
  },
  zonesRow: {
    flexDirection: "row",
    alignSelf: "stretch",
    gap: 8,
    alignItems: "stretch",
    justifyContent: "space-between",
  },
  zoneTile: {
    flex: 1,
    backgroundColor: "#FFE5DE",
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 10,
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  zoneTileText: {
    fontFamily: "Inter",
    fontWeight: "700",
    fontSize: 16,
    lineHeight: 22,
    textAlign: "center",
    alignSelf: "stretch",
  },
  zoneTileCompleted: {
    backgroundColor: "#D4EDDA", // Green background for completed
  },
  zoneTileIncomplete: {
    backgroundColor: "#FFE5DE", // Light orange from Figma for incomplete
  },
  zoneTileTextCompleted: {
    color: "#155724", // Green text for completed
  },
  zoneTileTextIncomplete: {
    color: "#C12C01", // Red text from Figma for incomplete
  },
});

export default WeeklyZonesCompleted;
